import {
  IAnimalValueLabelElement,
  TierartEnum,
  ValueLabelElement,
} from '@ps20-frontend/frontend-models';
import { AddVertragToAngebote } from '@ps20-frontend/shop-integration-redux';
import { AnimalIllness, AnimalIllnesses, Tiergeschlecht, Tierhaltung } from '@ps20/bff-models';
import { TgvSubprodukt } from './tgvSubprodukt';

export enum TgvDogBreedType {
  SELECTED = 'SELECTED',
  MIXED = 'MIXED',
  UNKNOWN = 'UNKNOWN',
}

export enum TgvDogMotherBreedType {
  SELECTED = 'SELECTED',
  MIXED_OR_UNKNOWN = 'MIXED_OR_UNKNOWN',
}

export enum TgvDogSize {
  SMALL = 'SMALL',
  LARGE = 'LARGE',
}

export interface TgvPosition {
  positionId: string;
}

export interface TiergruppePositions {
  tkvV2: TgvPosition[];
  thhHund: TgvPosition | null;
  thhPferd: TgvPosition | null;
}

export interface TgvTierUmfang {
  tkv: boolean;
  thh: boolean;
}

/** The per-animal answers of the form; everything except the identity and coverage of a card. */
export interface TgvTierForm {
  name: string;
  gender: Tiergeschlecht | null;
  /** DD.MM.YYYY */
  birthDate: string;
  dogBreedType: TgvDogBreedType | null;
  breed: ValueLabelElement | null;
  dogMotherBreedType: TgvDogMotherBreedType | null;
  dogMotherBreed: ValueLabelElement | null;
  dogSize: TgvDogSize | null;
  catKeepingType: Tierhaltung | null;
  hasIllnesses: boolean | null;
  selectedIllnesses: AnimalIllness[];
}

/**
 * The Vorvertrag (Ersatzgeschäft or Beibehaltung) an offer is created from: what the dashboard
 * hands to the shop with AddVertragToAngebote (vorvertragsNummern, beibehalten, Sofortschutz,
 * Linie, Kennzeichen, Zahlweise, ...), without the identity and the date of the offer.
 */
export type TgvVorvertrag = Omit<AddVertragToAngebote, 'appId' | 'angebotsId' | 'angebotsdatum'>;

export interface TgvTier extends TgvTierForm {
  cardId: string;
  tierart: TierartEnum | null;
  umfang: TgvTierUmfang;
  /** The Vorvertrag of the TKV V2 offer of this card; null for new business. */
  vorvertrag?: TgvVorvertrag | null;
}

export interface TgvVorschaeden {
  vorhanden: boolean | null;
  anzahl: number;
}

export interface TgvVorschaedenState {
  thhHund: TgvVorschaeden;
  thhPferd: TgvVorschaeden;
}

export interface TgvKunde {
  plz: string;
  ort: string;
  strasse: string;
  hausnummer: string;
  geburtsdatum: string;
}

/** Lookup data of the form; lives in the transient state because it is reloaded on every start. */
export interface TgvKatalog {
  dogBreeds: IAnimalValueLabelElement[];
  horseBreeds: ValueLabelElement[];
  catBreeds: ValueLabelElement[];
  thhDogBreeds: ValueLabelElement[];
  thhHorseBreeds: ValueLabelElement[];
  animalIllnesses: AnimalIllnesses | null;
}

export interface TiergruppeState {
  selectedSubprodukt: TgvSubprodukt;
  positions: TiergruppePositions;
  tiere: TgvTier[];
  vorschaeden: TgvVorschaedenState;
  kunde: TgvKunde;
  /** angebotsId of the TKV V2 offer created for a card, by cardId. */
  angebotsIds: Record<string, string>;
  /** Whether the THH offers were already added to the Warenkorb. */
  thhAngelegt: { thhHund: boolean; thhPferd: boolean };
  /**
   * The Vorvertrag of the shared THH offers (null for new business). Not set in states that were
   * saved before; read as null then.
   */
  thhVorvertrag?: { thhHund: TgvVorvertrag | null; thhPferd: TgvVorvertrag | null };
  /**
   * The offers that were taken over into the dashboard by a finished run: the cardId of a TKV V2
   * offer, the app (THH_HUND / THH_PFERD) for the THH offers. They are not removed when a later run fails.
   */
  uebernommen: string[];
  /** Deleted only after the take-over, so that going back to the shop keeps these offers. */
  deferredDeletion: string[];
}
